// WhatsAppActionSheet — iter89 (Phase 1).
//
// Singleton bottom sheet that handles all three WhatsApp action types:
//   • ride     — passenger circles + bag pills, no typing
//   • business — people circles + read-only pre-filled message
//   • vendor   — editable pre-filled text area
//
// Guests get an inline verification step BEFORE the Send button is active.
// We mock the WhatsApp delivery in Phase 1 — the backend returns a `mock_link`
// the frontend surfaces in a toast (until a real WA sender is wired).
//
// Usage:
//   const { openWA } = useWhatsAppSheet();
//   openWA({ type: "ride", destination: {...}, businessId? });
//
// The sheet builds the final message itself and opens https://wa.me/<phone>?text=…
// after verification (or immediately if the user is already authed).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Car, MessageCircle, ShoppingBag } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { formatPhoneWithFlag } from "../lib/formatPhone";
import { getWaAuthEnabled } from "../lib/waAuth";
import { openWhatsApp, cleanPhone } from "../lib/openWhatsApp";
import {
  reverseGeocode, getCurrentCoords, readLastPickup, writeLastPickup,
  stashPendingRide,
} from "../lib/ridePickup";

// User-requested country priority. All others appear alphabetically afterwards.
const COUNTRY_PRIORITY = ["CU", "CA", "RU", "DE", "FR", "IT", "ES", "MX", "GB", "US", "AR"];

const WAContext = createContext({ openWA: () => {}, openViewPref: () => {} });
export const useWhatsAppSheet = () => useContext(WAContext);

export function WhatsAppSheetProvider({ children }) {
  const [waState, setWaState] = useState(null); // {type, destination, businessId, vendorName}
  const [vpOpen, setVpOpen] = useState(false);
  const [vpSuggested, setVpSuggested] = useState("tourist");

  const openWA = useCallback((args) => setWaState(args), []);
  const openViewPref = useCallback((suggested = "tourist") => {
    setVpSuggested(suggested); setVpOpen(true);
  }, []);

  const ctx = useMemo(() => ({ openWA, openViewPref }), [openWA, openViewPref]);

  return (
    <WAContext.Provider value={ctx}>
      {/* Drain the legacy `sendRideRequest/sendBusinessContact/sendVendorContact`
          event queue so non-hook call sites still work. */}
      <QueueDrain openWA={openWA} />
      {children}
      {waState && createPortal(
        <Sheet state={waState} onClose={() => setWaState(null)} onVerified={(suggested) => {
          setWaState(null);
          if (suggested) openViewPref(suggested);
        }} />,
        document.body,
      )}
      {vpOpen && createPortal(
        <ViewPrefSheet suggested={vpSuggested} onClose={() => setVpOpen(false)} />,
        document.body,
      )}
    </WAContext.Provider>
  );
}

function Sheet({ state, onClose, onVerified }) {
  const { t, lang } = useI18n();
  const [pax, setPax] = useState(() => parseInt(localStorage.getItem("habano.ride.lastPassengers") || "1", 10));
  const [bags, setBags] = useState(() => localStorage.getItem("habano.ride.lastLuggage") || "none");
  const [vendorText, setVendorText] = useState(() => lang === "es" ? "Hola, me gustaría saber más." : "Hi, I would like to know more.");
  const [bizPeople, setBizPeople] = useState(1);

  // iter153 — Pickup location state. Prefers a caller-provided pickup
  // (e.g. from the /car/map pickup-mode handoff), then falls back to
  // the last-known pickup in localStorage, then fires a fresh GPS
  // detection when the sheet opens for a ride.
  const [pickup, setPickup] = useState(() => state.pickup || readLastPickup() || null);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [pickupError, setPickupError] = useState(false);

  // Verification state
  const [needsVerify, setNeedsVerify] = useState(null); // null = checking, true/false otherwise
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [waitingForLink, setWaitingForLink] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    // iter103 — VIP requests always require WA verification because they
    // create a server-side reminder tied to the user account. All other
    // popup types (ride / business / vendor) skip verification entirely
    // when the admin has disabled WhatsApp auth.
    getMe().then((me) => {
      if (me) { setNeedsVerify(false); return; }
      if (state.type === "vip_request") { setNeedsVerify(true); return; }
      const waEnabled = getWaAuthEnabled();
      setNeedsVerify(waEnabled);
    });
    return () => { document.body.style.overflow = ""; };
  }, [state.type]);

  // iter153 — On open (ride only), detect pickup GPS if we don't already
  // have one from the caller / last-known cache. Reverse-geocode to a
  // readable address. GPS denied or offline → show "unavailable" — never
  // block the ride flow.
  useEffect(() => {
    if (state.type !== "ride") return;
    if (pickup?.address) return;      // already set
    if (pickup?.lat && pickup?.lng && !pickup.address) {
      // Have coords but no address — resolve just the address.
      setPickupLoading(true);
      (async () => {
        const address = await reverseGeocode(pickup.lat, pickup.lng, lang);
        const next = { ...pickup, address };
        setPickup(next); writeLastPickup(next);
        setPickupLoading(false);
      })();
      return;
    }
    setPickupLoading(true); setPickupError(false);
    (async () => {
      try {
        const { lat, lng } = await getCurrentCoords();
        const address = await reverseGeocode(lat, lng, lang);
        const next = { lat, lng, address };
        setPickup(next); writeLastPickup(next);
      } catch {
        setPickupError(true);
      } finally {
        setPickupLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.type]);

  const buildMessage = () => {
    const d = state.destination || { name: "" };
    // iter143 — Build a Google Maps link from destination lat/lng so the
    // driver receives the exact coordinates and never needs to ask the
    // passenger for directions. Falls back gracefully when coords are
    // missing (omit the line — never break the message).
    const hasCoords = Number.isFinite(d.lat) && Number.isFinite(d.lng);
    const mapsUrl = hasCoords ? `https://maps.google.com/?q=${d.lat},${d.lng}` : "";
    // iter153 — Pickup section. Only included when we have at least a
    // resolved address OR coordinates. Never blocks the message.
    const hasPickup = pickup && (pickup.address || (Number.isFinite(pickup.lat) && Number.isFinite(pickup.lng)));
    const pickupMapsUrl = hasPickup && Number.isFinite(pickup?.lat) && Number.isFinite(pickup?.lng)
      ? `https://maps.google.com/?q=${pickup.lat},${pickup.lng}`
      : "";
    if (state.type === "ride") {
      const luggage = bags === "none"
        ? (lang === "es" ? "sin equipaje" : "no luggage")
        : bags === "1_2"
          ? (lang === "es" ? "1-2 maletas" : "1-2 bags")
          : (lang === "es" ? "3+ maletas" : "3+ bags");
      if (lang === "es") {
        const lines = ["Hola, me gustaría solicitar un viaje.", ""];
        if (hasPickup) {
          lines.push(`🚖 Recogerme en: ${pickup.address || `${pickup.lat.toFixed(4)}, ${pickup.lng.toFixed(4)}`}`);
          if (pickupMapsUrl) lines.push(`📍 ${pickupMapsUrl}`);
          lines.push("");
        }
        lines.push(`🏁 Destino: ${d.name}`);
        if (mapsUrl) lines.push(`📍 ${mapsUrl}`);
        else if (d.address) lines.push(`📍 ${d.address}`);
        lines.push("");
        lines.push(`Somos ${pax} ${pax === 1 ? "persona" : "personas"} · ${luggage}`);
        lines.push("");
        lines.push("Habano App");
        return lines.join("\n");
      }
      const lines = ["Hi, I would like to request a ride.", ""];
      if (hasPickup) {
        lines.push(`🚖 Pick up at: ${pickup.address || `${pickup.lat.toFixed(4)}, ${pickup.lng.toFixed(4)}`}`);
        if (pickupMapsUrl) lines.push(`📍 ${pickupMapsUrl}`);
        lines.push("");
      }
      lines.push(`🏁 Going to: ${d.name}`);
      if (mapsUrl) lines.push(`📍 ${mapsUrl}`);
      else if (d.address) lines.push(`📍 ${d.address}`);
      lines.push("");
      lines.push(`We are ${pax} ${pax === 1 ? "passenger" : "passengers"} · ${luggage}`);
      lines.push("");
      lines.push("Habano App");
      return lines.join("\n");
    }
    if (state.type === "business") {
      if (lang === "es") return `Hola, me gustaría visitar ${d.name}. Somos ${bizPeople === 5 ? "5+" : bizPeople} personas.\nEnviado desde Habano App — habano.app`;
      return `Hi, I would like to visit ${d.name}. We are ${bizPeople === 5 ? "5+" : bizPeople} people.\nSent via Habano App — habano.app`;
    }
    // vendor — editable text
    return `${vendorText}\n${lang === "es" ? "Enviado desde Habano App — habano.app" : "Sent via Habano App — habano.app"}`;
  };

  const fireWhatsApp = async () => {
    const { data } = await api.get("/public-settings").catch(() => ({ data: {} }));
    let phoneTarget;
    if (state.type === "ride") {
      phoneTarget = data?.whatsapp_general_ride || data?.transportation_whatsapp || data?.whatsapp_support;
    } else {
      // business / vendor — use the business's own whatsapp if present, else the platform support number
      phoneTarget = state.destination?.whatsapp || data?.whatsapp_support;
    }
    phoneTarget = cleanPhone(phoneTarget);
    if (!phoneTarget || phoneTarget.length < 8) {
      toast.error(lang === "es" ? "Servicio no disponible" : "Service not available");
      return;
    }
    const msg = buildMessage();
    // persist ride choices
    if (state.type === "ride") {
      try {
        localStorage.setItem("habano.ride.lastPassengers", String(pax));
        localStorage.setItem("habano.ride.lastLuggage", bags);
      } catch { /* ignore */ }
    }

    // iter139 — Open WhatsApp FIRST, synchronously, inside the click
    // handler. This is required for iOS Safari and Chrome on Android:
    // any `api.post` microtask before `window.open` causes the popup to
    // be silently blocked. Logging now runs AFTER the open as a true
    // fire-and-forget side effect.
    const openedUrl = openWhatsApp(phoneTarget, msg);
    if (!openedUrl) {
      toast.error(lang === "es" ? "No se pudo abrir WhatsApp" : "Could not open WhatsApp");
      return;
    }

    api.post("/whatsapp-activity", {
      service_type: state.type === "ride" ? "ride" : (state.type === "vendor" ? "vendor_contact" : "business_contact"),
      related_business_id: state.businessId || null,
      related_business_name: state.destination?.name,
      destination: state.destination?.name,
      full_message: msg,
      language: lang,
      source: state.source || "wa-sheet",
    }).catch(() => {});
    // iter134 — Log every ride request to the rides history (works for
    // guests and authed users; guests are tracked via a stable client_id).
    if (state.type === "ride") {
      try {
        let cid = localStorage.getItem("habano.client_id");
        if (!cid) {
          cid = (window.crypto?.randomUUID?.() || `cid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
          localStorage.setItem("habano.client_id", cid);
        }
        api.post("/rides/log", {
          destination_name: state.destination?.name || "Ride",
          destination_address: state.destination?.address || null,
          destination_business_id: state.businessId || null,
          lat: state.destination?.lat ?? null,
          lng: state.destination?.lng ?? null,
          source: state.source || "wa-sheet",
          client_id: cid,
          // iter142 — additive: persist the dispatch phone so the SOS
          // button can re-open WhatsApp to the same driver number.
          phone_target: phoneTarget,
        }).then(({ data }) => {
          // iter142 — additive: notify the RideTrackingProvider so it can
          // show the "Are you waiting for a ride?" popup. No-op if no
          // listener is mounted.
          const rideId = data?.ride?.id;
          if (rideId) {
            try {
              window.dispatchEvent(new CustomEvent("habano:ride-dispatched", {
                detail: { rideId, phoneTarget, destination: state.destination?.name },
              }));
            } catch { /* ignore */ }
          }
        }).catch(() => {});
      } catch { /* ignore */ }
    }
    toast.success(lang === "es" ? "Abriendo WhatsApp…" : "Opening WhatsApp…");
    onClose();
  };

  const handleSend = async () => {
    if (state.type === "vip_request") {
      // VIP request is only valid with a verified account. Always run the
      // verification path; on success the server-side VIP record is created
      // via the standard /vip-reminder POST executed after auth/wa/verify.
      if (!phone || !isValidPhoneNumber(phone)) {
        toast.error(lang === "es" ? "Número inválido" : "Invalid phone number");
        return;
      }
      setSending(true);
      try {
        // Stash the intent so WaVerify can complete the VIP request after auth.
        try {
          localStorage.setItem("habano.pending_action", JSON.stringify({
            kind: "vip_request",
            business_id: state.businessId || state.destination?.id,
            business_name: state.destination?.name,
            created_at: new Date().toISOString(),
          }));
        } catch { /* ignore */ }
        const { data } = await api.post("/auth/wa/start", { phone_e164: phone });
        if (data.rate_limited) {
          toast.error(lang === "es" ? "Demasiados intentos — espera una hora" : "Too many requests — wait an hour");
          return;
        }
        try { localStorage.setItem("habano.lastPhone", phone); } catch { /* ignore */ }
        setWaitingForLink(true);
        toast.success(lang === "es" ? "Revisa WhatsApp para tu enlace" : "Check WhatsApp for your link");
        console.log("[Habano dev] verify link:", data.mock_link);
      } catch (e) {
        toast.error(lang === "es" ? "No se pudo enviar el enlace" : "Could not send link");
      } finally { setSending(false); }
      return;
    }

    if (needsVerify) {
      // Trigger verification
      if (!phone || !isValidPhoneNumber(phone)) {
        toast.error(lang === "es" ? "Número inválido" : "Invalid phone number");
        return;
      }
      setSending(true);
      try {
        const { data } = await api.post("/auth/wa/start", { phone_e164: phone });
        if (data.rate_limited) {
          toast.error(lang === "es" ? "Demasiados intentos — espera una hora" : "Too many requests — wait an hour");
          return;
        }
        try { localStorage.setItem("habano.lastPhone", phone); } catch { /* ignore */ }
        setWaitingForLink(true);
        if (data.existing_account) {
          toast.message(lang === "es" ? "Ya tienes una cuenta — toca para iniciar sesión" : "You already have an account — tap to log in");
        } else {
          toast.success(lang === "es" ? "Revisa WhatsApp para tu enlace" : "Check WhatsApp for your link");
        }
        console.log("[Habano dev] verify link:", data.mock_link);
      } catch (e) {
        toast.error(lang === "es" ? "No se pudo enviar el enlace" : "Could not send link");
      } finally {
        setSending(false);
      }
      return;
    }
    fireWhatsApp();
  };

  const titleText = state.type === "ride"
    ? (lang === "es" ? "Solicitar un Conductor" : "Request a Driver")
    : state.type === "vip_request"
      ? (lang === "es" ? "Un paso rápido" : "One quick step")
      : (lang === "es" ? `Contactar ${state.destination?.name || ""}` : `Contact ${state.destination?.name || ""}`);

  const sendLabel = needsVerify
    ? (lang === "es" ? "Enviar enlace de WhatsApp" : "Send WhatsApp Link")
    : (lang === "es" ? "Solicitar Auto Ahora" : "Request Car Now");

  return (
    <div data-testid="wa-sheet" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", background: "#171717", borderTopLeftRadius: 24, borderTopRightRadius: 24, borderTop: "1px solid rgba(232,184,75,0.3)", padding: "10px 18px 24px", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 16px" }} />
        <button type="button" data-testid="wa-sheet-close" onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", color: "#B0AAA3", padding: 6 }}>
          <X className="w-5 h-5" />
        </button>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: state.type === "ride" ? 22 : 20, color: "#F5F1E8", lineHeight: 1.15, marginBottom: 16 }}>
          {titleText}
        </h2>

        {/* Destination card (read-only) for ride */}
        {state.type === "ride" && state.destination && (
          <div style={{ background: "#0d0d0d", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: "10px 12px", marginBottom: 18 }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>{lang === "es" ? "Destino" : "Destination"}</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 500 }}>{state.destination.name}</div>
            {state.destination.address && <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}>{state.destination.address}</div>}
          </div>
        )}

        {/* iter153 — Pickup location card (ride only). GPS auto-detect
            + reverse-geocode. "Change →" hands off to /car/map?mode=pickup. */}
        {state.type === "ride" && (
          <div
            data-testid="wa-pickup-card"
            style={{ background: "#0d0d0d", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: "10px 12px", marginBottom: 18 }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                  {t("ride_pickup_label")}
                </div>
                {pickupLoading ? (
                  <div
                    data-testid="wa-pickup-loading"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", fontStyle: "italic" }}
                  >
                    {t("ride_pickup_loading")}
                  </div>
                ) : pickupError || !pickup?.address ? (
                  <div
                    data-testid="wa-pickup-unavailable"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}
                  >
                    {t("ride_pickup_unavailable")}
                  </div>
                ) : (
                  <div
                    data-testid="wa-pickup-address"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {pickup.address}
                  </div>
                )}
              </div>
              <button
                type="button"
                data-testid="wa-pickup-change"
                onClick={() => {
                  stashPendingRide({ destination: state.destination, source: state.source || "wa-sheet" });
                  onClose();
                  window.location.href = "/car/map?mode=pickup";
                }}
                style={{
                  background: "transparent", border: "none",
                  color: "#E8B84B",
                  fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                  padding: "8px 4px", minHeight: 48,
                  whiteSpace: "nowrap",
                }}
              >
                {t("ride_pickup_change")}
              </button>
            </div>
          </div>
        )}

        {/* Contact card (business/vendor) — surfaces the destination's number with flag */}
        {(state.type === "business" || state.type === "vendor") && state.destination?.whatsapp && (
          <div data-testid="wa-contact-card" style={{ background: "#0d0d0d", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: "10px 12px", marginBottom: 18 }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
              {lang === "es" ? "Contacto" : "Contact"}
            </div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 500 }}>{state.destination.name || state.vendorName || ""}</div>
            <div data-testid="wa-contact-number" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#D8D2BF", marginTop: 2 }}>
              {formatPhoneWithFlag(state.destination.whatsapp)}
            </div>
          </div>
        )}

        {/* VIP request — verification-only block */}
        {state.type === "vip_request" && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#D8D2BF", lineHeight: 1.5, marginBottom: 12 }}>
              {lang === "es"
                ? "Verifica tu WhatsApp para solicitar acceso VIP — esto crea tu cuenta gratuita de Habano."
                : "Verify your WhatsApp to request VIP access — this creates your free Habano account."}
            </p>
            {state.destination?.name && (
              <div data-testid="wa-vip-context" style={{ background: "#0d0d0d", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: "10px 12px", marginBottom: 16 }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                  {lang === "es" ? "Acceso VIP a" : "VIP access to"}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 500 }}>{state.destination.name}</div>
              </div>
            )}
            <div data-testid="wa-phone-input" style={{ background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.30)", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
              <PhoneInput
                defaultCountry="CU"
                value={phone}
                onChange={(v) => setPhone(v || "")}
                international
                countryCallingCodeEditable={false}
                countries={[...COUNTRY_PRIORITY]}
                addInternationalOption={false}
              />
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3" }}>
              {lang === "es" ? "Solo usamos tu número para verificarte — nunca lo compartimos" : "We only use your number to verify you — we never share it"}
            </p>
            {waitingForLink && (
              <p data-testid="wa-vip-waiting" style={{ marginTop: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B" }}>
                {lang === "es" ? "Revisa WhatsApp — el enlace expira en 15 minutos." : "Check WhatsApp — the link expires in 15 minutes."}
              </p>
            )}
          </div>
        )}

        {/* Ride: pax + bags */}
        {state.type === "ride" && (
          <>
            <Eyebrow>{lang === "es" ? "Pasajeros" : "Passengers"}</Eyebrow>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {[1, 2, 3, 4].map((n) => (
                <button key={n} type="button" data-testid={`wa-pax-${n}`} onClick={() => setPax(n)}
                  style={{ width: 52, height: 52, borderRadius: 999, border: pax === n ? "none" : "1px solid rgba(232,184,75,0.4)", background: pax === n ? "#E8B84B" : "transparent", color: pax === n ? "#0B0B0B" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 700 }}>
                  {n}
                </button>
              ))}
            </div>
            <Eyebrow>{lang === "es" ? "Equipaje" : "Luggage"}</Eyebrow>
            <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
              {[{k:"none", en:"None", es:"Nada"},{k:"1_2", en:"1-2 bags", es:"1-2 maletas"},{k:"3p", en:"3+ bags", es:"3+ maletas"}].map((b) => (
                <button key={b.k} type="button" data-testid={`wa-bags-${b.k}`} onClick={() => setBags(b.k)}
                  style={{ minHeight: 48, padding: "0 18px", borderRadius: 999, border: bags === b.k ? "none" : "1px solid rgba(232,184,75,0.4)", background: bags === b.k ? "#E8B84B" : "transparent", color: bags === b.k ? "#0B0B0B" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
                  {lang === "es" ? b.es : b.en}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Business: people + read-only msg preview */}
        {state.type === "business" && (
          <>
            <Eyebrow>{lang === "es" ? "Personas" : "People"}</Eyebrow>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`wa-biz-people-${n}`} onClick={() => setBizPeople(n)}
                  style={{ width: 52, height: 52, borderRadius: 999, border: bizPeople === n ? "none" : "1px solid rgba(232,184,75,0.4)", background: bizPeople === n ? "#E8B84B" : "transparent", color: bizPeople === n ? "#0B0B0B" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
                  {n === 5 ? "5+" : n}
                </button>
              ))}
            </div>
            <div style={{ background: "#0d0d0d", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: 12, marginBottom: 18, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
              {buildMessage()}
            </div>
          </>
        )}

        {/* Vendor: editable textarea */}
        {state.type === "vendor" && (
          <textarea
            data-testid="wa-vendor-text"
            value={vendorText}
            onChange={(e) => setVendorText(e.target.value)}
            rows={3}
            style={{ width: "100%", background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 10, padding: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", marginBottom: 18, resize: "vertical", minHeight: 90 }}
          />
        )}

        {/* Verification block (guests only, except vip_request which renders its own) */}
        {needsVerify && state.type !== "vip_request" && (
          <div style={{ borderTop: "0.5px solid rgba(232,184,75,0.2)", paddingTop: 16, marginBottom: 12 }}>
            <Eyebrow>{lang === "es" ? "Un paso rápido" : "One quick step"}</Eyebrow>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", marginBottom: 12 }}>
              {lang === "es" ? "Verifica tu WhatsApp para enviar esta solicitud — esto crea tu cuenta gratuita de Habano." : "Verify your WhatsApp to send this request — this creates your free Habano account."}
            </p>
            <div data-testid="wa-phone-input" style={{ background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
              <PhoneInput
                defaultCountry="CU"
                value={phone}
                onChange={(v) => setPhone(v || "")}
                international
                countryCallingCodeEditable={false}
                countries={[...COUNTRY_PRIORITY]}
                addInternationalOption={false}
                style={{ color: "#F5F1E8" }}
              />
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3" }}>
              {lang === "es" ? "Solo usamos tu número para verificarte — nunca lo compartimos" : "We only use your number to verify you — we never share it"}
            </p>
            {waitingForLink && (
              <p style={{ marginTop: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B" }}>
                {lang === "es" ? "Revisa WhatsApp — el enlace expira en 15 minutos." : "Check WhatsApp — the link expires in 15 minutes."}
              </p>
            )}
          </div>
        )}

        {/* Buttons */}
        <button
          type="button"
          data-testid="wa-send-btn"
          onClick={handleSend}
          disabled={sending || needsVerify === null || (state.type === "vip_request" ? !isValidPhoneNumber(phone || "") : (needsVerify && !isValidPhoneNumber(phone || "")))}
          style={{
            width: "100%", minHeight: 48, borderRadius: 10, border: "none",
            background: sending || (needsVerify && !isValidPhoneNumber(phone || "")) ? "#3a2e15" : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
            color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            opacity: sending ? 0.6 : 1, marginBottom: 8,
          }}>
          {state.type === "ride" ? <Car className="w-4 h-4" /> : state.type === "vendor" ? <ShoppingBag className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
          {sendLabel}
        </button>
        <button type="button" data-testid="wa-cancel-btn" onClick={onClose}
          style={{ width: "100%", minHeight: 48, borderRadius: 10, background: "transparent", border: "1.5px solid rgba(232,184,75,0.5)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
          {lang === "es" ? "Cancelar" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

function ViewPrefSheet({ suggested, onClose }) {
  const { lang } = useI18n();
  const choose = async (view) => {
    try { localStorage.setItem("habano.view", view); } catch { /* ignore */ }
    await api.post("/me/view-preference", { view }).catch(() => {});
    toast.success(lang === "es" ? "Listo" : "All set");
    onClose();
  };
  return (
    <div data-testid="view-pref-sheet" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 210, display: "flex", alignItems: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", background: "#171717", borderTopLeftRadius: 24, borderTopRightRadius: 24, borderTop: "1px solid rgba(232,184,75,0.3)", padding: "10px 18px 24px" }}>
        <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 16px" }} />
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", textAlign: "center", marginBottom: 22 }}>
          {lang === "es" ? "¿Estás de visita en La Habana o vives aquí?" : "Are you visiting Havana or do you live here?"}
        </h2>
        <div style={{ display: "grid", gap: 12 }}>
          <button type="button" data-testid="vp-tourist" onClick={() => choose("tourist")}
            style={{ minHeight: 56, borderRadius: 12, border: "none", background: suggested === "tourist" ? "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)" : "#1a1a1a", color: suggested === "tourist" ? "#0B0B0B" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
            {lang === "es" ? "🧳 Estoy de visita" : "🧳 I am visiting"}
          </button>
          <button type="button" data-testid="vp-local" onClick={() => choose("local")}
            style={{ minHeight: 56, borderRadius: 12, border: "none", background: suggested === "local" ? "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)" : "#1a1a1a", color: suggested === "local" ? "#0B0B0B" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>
            {lang === "es" ? "🏠 Vivo en La Habana" : "🏠 I live in Havana"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Eyebrow({ children }) {
  return <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>{children}</div>;
}

function QueueDrain({ openWA }) {
  useEffect(() => {
    const handler = (e) => openWA(e.detail);
    window.addEventListener("habano:wa-sheet-open", handler);
    return () => window.removeEventListener("habano:wa-sheet-open", handler);
  }, [openWA]);
  return null;
}
